import { feedRowHasDate, feedScrape, listingFacts, type FeedScrape, type ListingFacts } from '../feed/listing.ts';
import type { Playlist, PlaylistEntry } from '../model/playlist.ts';
import { videoBadgeOf, type VideoSummary } from '../model/video.ts';

/**
 * §8's Playlists row, viewing half: how `/api/v1/playlists/{plid}` pages, and what one of its entry
 * rows is *allowed to claim*.
 *
 * In core rather than in the playlist screen for the reason `channel/tabs.ts` and `search/query.ts`
 * are: every line is a fact measured on the wire, Compose has to act on the identical facts, and a
 * decision table written inside an event handler is a decision table nobody tests.
 *
 * Everything below was measured against `invidious.example.com` on 2026-07-29 — **117 playlists and
 * 8 101 entry rows** (API reference §7g). Three of the measurements changed the design and are called
 * out where they apply. The one to know before reading anything else: the pages **overlap**, so this
 * is the first list in the app that needs a real dedupe pass.
 */

/* ------------------------------------------------------------------------------------------------
 * Requests
 * ---------------------------------------------------------------------------------------------- */

export function playlistPath(playlistId: string): string {
  return `/api/v1/playlists/${encodeURIComponent(playlistId)}`;
}

/** 1-based, like search's. `page=0` is not "the first page" — see {@link playlistWireParams}. */
export const PLAYLIST_FIRST_PAGE = 1;

/** How much further into the playlist each page starts than the one before it. Measured, see below. */
const PLAYLIST_PAGE_STRIDE = 100;

/**
 * What goes on the wire for one page.
 *
 * The parameter is honoured, unlike most of the ones this project has measured — but its
 * out-of-range behaviour is the `sort_by=popular` trap again, and worse, because there is no
 * unrecognised *value* to avoid:
 *
 * ```
 * GET /api/v1/playlists/PL6Lt…            →  200, 206 rows      ← 575-entry playlist
 * GET /api/v1/playlists/PL6Lt…?page=1     →  200, 206 rows        identical to the bare request
 * GET /api/v1/playlists/PL6Lt…?page=0     →  200, videos: []    ← emptied, not clamped
 * GET /api/v1/playlists/PL6Lt…?page=-1    →  200, videos: []
 * GET /api/v1/playlists/PL6Lt…?page=99    →  200, videos: []
 * GET /api/v1/playlists/PL6Lt…?page=abc   →  200, 206 rows        falls back to page 1
 * ```
 *
 * So a page number below 1 renders a 575-video playlist as an empty one, at `200`, with the whole
 * header intact — and the clamp is here rather than at the call site because every caller would need
 * it. Page 1 sends no parameter at all, which keeps the first request byte-identical to the bare one.
 */
export function playlistWireParams(page: number): Readonly<Record<string, string | null>> {
  const clamped = Number.isFinite(page) ? Math.max(PLAYLIST_FIRST_PAGE, Math.trunc(page)) : PLAYLIST_FIRST_PAGE;
  return clamped === PLAYLIST_FIRST_PAGE ? {} : { page: String(clamped) };
}

/**
 * A page number far past the end of any playlist, asked for on purpose: the emptied-not-clamped
 * behaviour above turns an out-of-range page into a **header-only read**.
 *
 * ```
 * GET /api/v1/playlists/UUXuqSBlHAE6Xw-yeJA0Tunw?page=100000
 *   → 200, 1 497 bytes, all 16 header keys, videoCount: 7271, videos: []
 * ```
 *
 * Measured on three playlists of 19, 575 and 7 271 entries (2026-08-07): the title, the author and
 * the count all arrive, and not one entry row does. That is the whole point — it costs ~1.5 KB where
 * page 1 costs ~250 KB, and the count it carries is what {@link playlistPageForIndex} needs before
 * it can name a page. A reader who has asked to walk a playlist from its end must not pay for its
 * beginning first.
 *
 * The number is arbitrary but has to stay well clear of a real page: 100 000 pages is 10 million
 * entries at the measured stride, against YouTube's own 5 000-entry ceiling.
 */
export const PLAYLIST_HEADER_PROBE_PAGE = 100_000;

/**
 * The page whose window is expected to hold `index`.
 *
 * **The stride is measured, not assumed**, and the window is twice it — page 1 covers `0..201`, and
 * every page after it starts 100 later than the one before while still returning ~200 rows:
 *
 * ```
 * 575-entry playlist          page 1  0..201     page 5  350..574
 *                             page 2  50..251    page 6  450..574
 *                             page 3  150..355   page 7  550..574
 *                             page 4  250..449   page 8  (empty)
 * ```
 *
 * So a page's first index is `(page - 2) × 100 + 50` from page 2 on, and this function inverts that
 * and takes the **largest** page starting at or below the target — which puts the target in the
 * first 100 of a ~200-row window, so the page contains it with room to spare either side.
 *
 * Re-measured 2026-08-07 against five playlists of 575, 830, 1 291, 1 837 and 7 271 entries: on every
 * one of them `playlistPageForIndex(videoCount - 1)` returned the page holding the last entry, and
 * the page after it was empty.
 *
 * It is arithmetic over an observed stride rather than a contract, and the authenticated endpoint
 * strides 50 with a 100-row window where the mirrored one strides 100 with a 200-row window, so this
 * is only ever the **opening** guess: {@link playlistWalk} measures the real stride off the pages
 * that came back and brackets the guess against them. A page landing somewhere else than expected
 * therefore costs one request, because `foldPlaylistPages` keys by `index` and takes whatever arrives.
 */
export function playlistPageForIndex(index: number): number {
  if (!Number.isFinite(index) || index < PLAYLIST_PAGE_STRIDE / 2) return PLAYLIST_FIRST_PAGE;
  return Math.floor((Math.trunc(index) - PLAYLIST_PAGE_STRIDE / 2) / PLAYLIST_PAGE_STRIDE) + 2;
}

/* ------------------------------------------------------------------------------------------------
 * Paging — the fold, and why it has to dedupe
 * ---------------------------------------------------------------------------------------------- */

/**
 * The accumulated entry list, folded from however many pages have arrived.
 *
 * **The pages overlap, heavily and irregularly, and a single page repeats rows inside itself.**
 * Measured on a 575-entry playlist, seven pages:
 *
 * ```
 * page 1  206 rows  index 0..201    4 rows repeated within the page (…101 102 103 100 101…)
 * page 2  204 rows  index 50..251   154 of its 204 rows were already on page 1
 * page 3  208 rows  index 150..355  104 already seen
 * page 4  206 rows  index 250..449  112 already seen
 * page 5  225 rows  index 350..574  100 already seen — and this page reaches the end
 * page 6  150 rows  index 450..574  150 of 150 already seen
 * page 7   25 rows  index 550..574   25 of 25 already seen
 *
 * naive concatenation  1 224 rows        for a playlist of 575
 * ```
 *
 * So the stride is ~100 while the window is ~200, and one playlist returned **271 rows on page 1 for
 * a 266-entry playlist** — a single page holding more rows than the playlist has. Every list this app
 * has paged so far needed no dedupe (a channel tab: 720 videos over 12 pages, zero duplicates); this
 * one is 2.1× duplicated before it starts.
 *
 * **`index` is the key that makes it tractable, and it was verified as one rather than assumed.**
 * Across the seven pages above, `index → videoId` had **zero conflicts** and the 575 distinct indices
 * covered `0..574` **with no gaps**. Two identical fetches of page 2 were byte-for-byte identical, so
 * unlike `/api/v1/search` this endpoint is deterministic and the fold is reproducible.
 *
 * The rows are therefore keyed *and ordered* by `index`, not by arrival: page 1's own order is not
 * monotonic, so appending in arrival order would render a playlist that jumps backwards four rows in
 * the middle of its first screen.
 */
export interface PlaylistListing {
  /** Deduplicated by `index` and ordered by it — see above for why both halves are necessary. */
  readonly entries: readonly PlaylistEntry[];
  readonly pages: number;
  /** Rows as the API sent them, before dedupe. Kept because `rowsReceived - entries.length` is the finding. */
  readonly rowsReceived: number;
  readonly duplicateRows: number;
  /** R4 — records recognised and unusable, summed across pages. */
  readonly droppedCount: number;
  /**
   * The page to ask for next, or `null` when the list is complete.
   *
   * **The signal that always works is "the last page contributed no new `index`".** Not the row count:
   * pages run 13, 25, 150, 206, 271 rows with no relationship to position, so a `rows < pageSize` test
   * would stop a 575-entry playlist at page 6 having already had the answer at page 5 — or, on the
   * 266-entry playlist whose page 1 was 271 rows, never trigger at all. An empty page is subsumed by
   * the working rule: it adds no new index by definition, which is what makes small playlists (whose
   * page 2 really is `videos: []`) and large ones (whose page 6 is a pure repeat of page 5) one rule
   * rather than two.
   *
   * There is a **second, earlier** stop, and it is a proof rather than a heuristic: see
   * {@link playlistListingComplete}. Without it every playlist ends with one request that returns
   * nothing new, behind a "Load more" button offered on a list that is already whole.
   */
  readonly nextPage: number | null;
  /**
   * Every position from 0 up, with no holes. The precondition for reading `videoCount` as a total —
   * kept on the listing because it is also what makes the count trustworthy enough to *show*.
   */
  readonly gaplessFromZero: boolean;
}

/**
 * The list is whole: it holds `videoCount` entries covering `0..videoCount-1` with no gaps.
 *
 * This is the second stop condition, and it is worth the extra rule because the alternative is a
 * "Load more" button on a complete playlist that fetches 170 KB and adds nothing — measured, and the
 * first thing a driven browser showed about this page.
 *
 * **It is a completeness proof, not a size check, and that distinction is the whole safety argument.**
 * Requiring gapless coverage *from zero* means a `videoCount` that is too low cannot truncate anything
 * unless it happens to equal the length of a gapless prefix — and page 1 hands over ~200 rows
 * regardless of the playlist's length, so a 300-entry playlist claiming 180 would still arrive with
 * indices 0..201 and fail this test. Measured: on 11 of 12 playlists `videoCount` equalled a gapless
 * `0..count-1` exactly (575, 496, 437, 266, 210, 186, 180, 110, 48, 14, 13), and the twelfth — a
 * YouTube auto-mix — carries **no `videoCount` field at all**, which is why the absent case must fall
 * through to the page-by-page rule rather than being treated as zero.
 */
export function playlistListingComplete(
  listing: Pick<PlaylistListing, 'entries' | 'gaplessFromZero'>,
  videoCount: number | null,
): boolean {
  if (videoCount === null || !Number.isFinite(videoCount) || videoCount <= 0) return false;
  return listing.gaplessFromZero && listing.entries.length === videoCount;
}

/**
 * Fold the pages fetched so far. Each page is the whole `Playlist` record, because the endpoint
 * re-sends the header with every page — ~1.5 KB of title, author and description that the caller
 * reads from the first page and this function ignores.
 */
export function foldPlaylistPages(pages: readonly Playlist[]): PlaylistListing {
  const byIndex = new Map<number, PlaylistEntry>();
  let rowsReceived = 0;
  let droppedCount = 0;
  let lastPageAddedNew = false;

  pages.forEach((page, pageNumber) => {
    let added = 0;
    droppedCount += page.dropped.length;
    for (const entry of page.entries) {
      rowsReceived += 1;
      // First writer wins. `index → videoId` had zero conflicts over the measured pages, so the two
      // readings agree — and keeping the first means a re-fetch of an earlier page cannot reorder
      // rows the reader is already looking at.
      if (byIndex.has(entry.index)) continue;
      byIndex.set(entry.index, entry);
      added += 1;
    }
    if (pageNumber === pages.length - 1) lastPageAddedNew = added > 0;
  });

  const entries = [...byIndex.values()].sort((a, b) => a.index - b.index);

  const sofar = {
    entries,
    pages: pages.length,
    rowsReceived,
    duplicateRows: rowsReceived - entries.length,
    droppedCount,
    gaplessFromZero: entries.every((entry, position) => entry.index === position),
  };

  // The header is re-sent with every page, so any page's count will do; the first is read for the same
  // reason the screen reads its title from there.
  const complete = playlistListingComplete(sofar, pages[0]?.videoCount ?? null);

  return {
    ...sofar,
    nextPage:
      pages.length === 0
        ? PLAYLIST_FIRST_PAGE
        : complete || !lastPageAddedNew
          ? null
          : pages.length + PLAYLIST_FIRST_PAGE,
  };
}

/** For a `getNextPageParam`: the same answer as {@link PlaylistListing.nextPage}, without the fold. */
export function nextPlaylistPage(pages: readonly Playlist[]): number | null {
  return foldPlaylistPages(pages).nextPage;
}

/* ------------------------------------------------------------------------------------------------
 * Walking the list from one end
 * ---------------------------------------------------------------------------------------------- */

export interface PlaylistWalkOptions {
  /** Read from the last entry towards the first, rather than from the first towards the last. */
  readonly backward: boolean;
  /** Every page param already asked for, one per entry of `pages` and in the same order. */
  readonly requested: readonly number[];
  /**
   * The index the run has to contain. With one, the entries are the contiguous stretch **around** it
   * rather than from an end, and `backward` decides nothing.
   */
  readonly anchor?: number | null;
  /** Whether the reader has asked to see past the run's ends. */
  readonly reach?: { readonly before: boolean; readonly after: boolean };
}

/**
 * What a reader walking the list from one of its ends may see, and the page that extends it.
 *
 * Two things separate this from {@link foldPlaylistPages}, and both come from the same fact: **the
 * fetched pages are a bag, not a sequence.** One cache holds whatever both directions have asked for,
 * so a playlist opened forwards and then reversed has page 1 and page 6 in hand and nothing between
 * them.
 *
 * - **The entries are the contiguous run anchored at the end being read from** — from index 0
 *   forwards, from `videoCount - 1` backwards — and everything past the first hole is withheld. A
 *   reader scrolling the reversed list must reach entry 450 before entry 449 appears; showing page 1's
 *   rows directly under page 6's would draw a list that skips 250 entries without saying so.
 *   With an `anchor` the run is the stretch **around** that index instead, so a reader opening a
 *   playlist at entry 450 reads the pages either side of 450 and never pays for its first 450 rows.
 * - **The next page is named from the index the run stops at**, not from how many pages have been
 *   fetched. Reversed, that is the whole point: naming the end's page from `videoCount` costs one
 *   request where walking to the end of a 7 271-entry playlist costs 73 of ~250 KB. Forwards it is
 *   worth it anyway, because the window is twice the stride — a 575-entry playlist arrives in pages
 *   1, 3 and 5 rather than 1 through 5.
 *
 * The page numbers are **arithmetic over a stride the endpoint never promised**, so this reads the
 * stride back off the pages that arrived instead of trusting one constant: the mirrored endpoint
 * strides ~100 with a ~200-row window, the authenticated one strides 50 with a 100-row window, and a
 * guess that lands short is bracketed and bisected rather than repeated. {@link playlistPageForIndex}
 * supplies the opening guess, which is all it is ever asked for.
 *
 * With no `videoCount` there is no end to aim at — a YouTube auto-mix carries none (§7g, 1 of 12
 * playlists measured) — so the walk is the plain forward one in both directions and the caller
 * reverses whatever arrives. The mixes are ~25 entries.
 */
export function playlistWalk(
  pages: readonly Playlist[],
  options: PlaylistWalkOptions,
): PlaylistListing {
  const folded = foldPlaylistPages(pages);
  const declared = pages[0]?.videoCount ?? null;
  const videoCount =
    declared !== null && Number.isFinite(declared) && declared > 0 ? declared : null;
  const anchor = walkAnchor(options.anchor ?? null, videoCount);

  const entries =
    anchor !== null
      ? runAround(folded.entries, anchor)
      : options.backward && videoCount !== null
        ? runEndingAt(folded.entries, videoCount - 1)
        : runFromZero(folded.entries);

  return {
    // `rowsReceived` and `duplicateRows` stay the fold's: they describe what the endpoint sent, which
    // is not affected by how much of it the reader is allowed to see yet.
    ...folded,
    entries,
    gaplessFromZero: entries.every((entry, position) => entry.index === position),
    nextPage: pageAfterRun(pages, folded, entries, videoCount, options, anchor),
  };
}

/**
 * The anchor this walk can really use, or `null` for the walk from an end.
 *
 * **Without a `videoCount` there is nothing to anchor against.** The page arithmetic needs an end to
 * bracket its guesses between, and a list that declares none is a YouTube auto-mix of ~25 entries
 * (§7g, 1 of 12 measured) that arrives whole on page 1 — so the walk stays the plain forward one and
 * every row is there on the first response anyway.
 *
 * An index past the last one names no row, so it is no anchor either and the list is read from its
 * start. That is a route the reader typed or a `videoCount` that came back short, and both are better
 * answered with the beginning of the playlist than with an empty list and a walk hunting for an entry
 * that does not exist.
 */
function walkAnchor(anchor: number | null, videoCount: number | null): number | null {
  if (anchor === null || videoCount === null) return null;
  if (!Number.isInteger(anchor) || anchor < 0 || anchor > videoCount - 1) return null;
  return anchor;
}

/** The entries from index 0 up to the first hole. Empty when index 0 itself has not arrived. */
function runFromZero(entries: readonly PlaylistEntry[]): readonly PlaylistEntry[] {
  let length = 0;
  while (entries[length]?.index === length) length += 1;
  return length === entries.length ? entries : entries.slice(0, length);
}

/** The entries from `last` down to the first hole. Empty when `last` itself has not arrived. */
function runEndingAt(entries: readonly PlaylistEntry[], last: number): readonly PlaylistEntry[] {
  let start = entries.length;
  let expected = last;
  while (start > 0 && entries[start - 1]?.index === expected) {
    start -= 1;
    expected -= 1;
  }
  return start === 0 ? entries : entries.slice(start);
}

/**
 * The entries either side of `anchor`, up to the first hole in each direction. **Empty when `anchor`
 * itself has not arrived**, exactly as the two runs above are empty when their own end has not: a run
 * that does not hold the index the reader opened the list at is not the stretch they asked for, and
 * drawing the nearest one instead would put a list on screen that starts somewhere nobody named.
 */
function runAround(entries: readonly PlaylistEntry[], anchor: number): readonly PlaylistEntry[] {
  const at = entries.findIndex((entry) => entry.index === anchor);
  if (at === -1) return [];

  let start = at;
  let expected = anchor - 1;
  while (start > 0 && entries[start - 1]?.index === expected) {
    start -= 1;
    expected -= 1;
  }
  let end = at;
  expected = anchor + 1;
  while (entries[end + 1]?.index === expected) {
    end += 1;
    expected += 1;
  }
  return start === 0 && end === entries.length - 1 ? entries : entries.slice(start, end + 1);
}

function pageAfterRun(
  pages: readonly Playlist[],
  folded: PlaylistListing,
  run: readonly PlaylistEntry[],
  videoCount: number | null,
  options: PlaylistWalkOptions,
  anchor: number | null,
): number | null {
  if (pages.length === 0) return PLAYLIST_FIRST_PAGE;

  // A caller that does not track its page params has asked for them in order — every list in this app
  // did before there was a direction to choose.
  const requested =
    options.requested.length === pages.length
      ? options.requested
      : pages.map((_, position) => position + PLAYLIST_FIRST_PAGE);
  const real = requested.filter(
    (page) => page >= PLAYLIST_FIRST_PAGE && page < PLAYLIST_HEADER_PROBE_PAGE,
  );

  if (videoCount === null) {
    // Nothing to aim at, so the stop is the fold's own: the last page contributed no new index. The
    // probe page is not a page of the playlist, so a walk that has only asked for it starts at 1.
    if (real.length === 0) return PLAYLIST_FIRST_PAGE;
    return folded.nextPage === null ? null : Math.max(...real) + 1;
  }

  const observed = pageWindows(pages, requested);

  if (anchor !== null) {
    return pageAroundRun(anchor, run, observed, requested, videoCount, options.reach ?? null);
  }

  if (options.backward) {
    const lowest = run[0]?.index ?? videoCount;
    if (lowest <= 0) return null;
    return pageHolding(lowest - 1, observed, requested);
  }

  const highest = run[run.length - 1]?.index ?? -1;
  if (highest >= videoCount - 1) return null;
  return pageHolding(highest + 1, observed, requested);
}

/**
 * The page that grows an anchored run, or `null` when there is nothing left the reader has asked for.
 *
 * **The anchor's own page comes first**, and while it is missing no other page is worth a request: the
 * run is empty until that index arrives, so a page either side of it would be rows nothing can be
 * attached to.
 *
 * With the run in hand the two directions are only read when the reader has asked to see past that end,
 * which is what keeps opening a playlist at entry 450 to one request. **`after` before `before`** when
 * both are asked, and the order is fixed rather than arbitrary: forwards is the direction the list
 * plays in, so the rows the reader is about to reach matter before the ones they have already gone past.
 * A direction with nothing left in it falls through to the other rather than ending the walk, since a
 * reader who asked for both wants both filled.
 *
 * Both ends terminate on their own. Nothing is asked for past `videoCount - 1` or below index 0, and
 * every guess goes through {@link pageHolding}, which brackets it against the pages already in hand — so
 * an anchor's page that comes back empty closes the walk down rather than being asked for again.
 */
function pageAroundRun(
  anchor: number,
  run: readonly PlaylistEntry[],
  observed: PageWindows,
  requested: readonly number[],
  videoCount: number,
  reach: { readonly before: boolean; readonly after: boolean } | null,
): number | null {
  const first = run[0];
  const last = run[run.length - 1];
  if (first === undefined || last === undefined) return pageHolding(anchor, observed, requested);

  if (reach?.after === true && last.index < videoCount - 1) {
    const page = pageHolding(last.index + 1, observed, requested);
    if (page !== null) return page;
  }
  if (reach?.before === true && first.index > 0) {
    return pageHolding(first.index - 1, observed, requested);
  }
  return null;
}

/** Which indices one requested page actually came back with. */
interface PageWindow {
  readonly page: number;
  readonly first: number;
  readonly last: number;
}

interface PageWindows {
  /** Ordered by page number. */
  readonly filled: readonly PageWindow[];
  /** Pages that came back with no rows at all, which places them past the end of the playlist. */
  readonly emptied: readonly number[];
}

function pageWindows(pages: readonly Playlist[], requested: readonly number[]): PageWindows {
  const filled: PageWindow[] = [];
  const emptied: number[] = [];

  pages.forEach((page, position) => {
    const number = requested[position];
    if (number === undefined || number < PLAYLIST_FIRST_PAGE) return;
    // The probe page comes back empty by construction, so it is no evidence about where the playlist
    // ends and must not become one of the bounds below.
    if (number >= PLAYLIST_HEADER_PROBE_PAGE) return;

    let first: number | null = null;
    let last: number | null = null;
    for (const entry of page.entries) {
      if (first === null || entry.index < first) first = entry.index;
      if (last === null || entry.index > last) last = entry.index;
    }
    if (first === null || last === null) emptied.push(number);
    else filled.push({ page: number, first, last });
  });

  return { filled: filled.sort((a, b) => a.page - b.page), emptied };
}

/**
 * The page to ask for to reach `target`, or `null` when no page can still hold it.
 *
 * The estimate below is a guess, so it is boxed in by what already came back: the highest page whose
 * window ended before the target, and the lowest one that started past it. Once those two are
 * neighbours there is nothing left to ask. While they are not, the guess is clamped strictly between
 * them, and a page asked for there can only come back holding the target, ending before it or
 * starting after it — so every answer moves one of the two ends inward. The walk therefore cannot
 * repeat a request and cannot run on.
 */
function pageHolding(
  target: number,
  windows: PageWindows,
  requested: readonly number[],
): number | null {
  let below: number | null = null;
  let above: number | null = null;
  for (const window of windows.filled) {
    if (window.last < target && (below === null || window.page > below)) below = window.page;
    if (window.first > target && (above === null || window.page < above)) above = window.page;
  }
  for (const page of windows.emptied) {
    if (above === null || page < above) above = page;
  }
  if (below !== null && above !== null && above - below <= 1) return null;

  let guess = estimatePage(target, windows.filled);
  if (below !== null) guess = Math.max(guess, below + 1);
  if (above !== null) guess = Math.min(guess, above - 1);
  guess = Math.max(PLAYLIST_FIRST_PAGE, guess);
  if (above !== null && guess >= above) return null;
  // Unreachable while the bracket holds, and kept so that a response this code did not anticipate
  // costs a stopped walk instead of a request loop.
  return requested.includes(guess) ? null : guess;
}

function estimatePage(target: number, filled: readonly PageWindow[]): number {
  const [start, ...rest] = filled;
  if (start === undefined) return playlistPageForIndex(target);

  // Anchored on the page whose window starts nearest the target, so a stride that is wrong further
  // along the playlist costs one step rather than a search.
  let anchor = start;
  for (const window of rest) {
    if (Math.abs(window.first - target) < Math.abs(anchor.first - target)) anchor = window;
  }
  return anchor.page + Math.floor((target - anchor.first) / observedStride(filled));
}

function observedStride(filled: readonly PageWindow[]): number {
  // Page 1 starts half a stride below page 2 rather than a whole one (see {@link playlistPageForIndex}),
  // so it cannot be an end of the measurement. Two pages after it can, and the span between the
  // furthest apart of them averages out a single page that came back short.
  const past = filled.filter((window) => window.page > PLAYLIST_FIRST_PAGE);
  const low = past[0];
  const high = past[past.length - 1];
  if (low === undefined || high === undefined || high.page === low.page) return PLAYLIST_PAGE_STRIDE;

  const stride = Math.round((high.first - low.first) / (high.page - low.page));
  return Number.isFinite(stride) && stride >= 1 ? stride : PLAYLIST_PAGE_STRIDE;
}

/* ------------------------------------------------------------------------------------------------
 * What an entry row may state
 * ---------------------------------------------------------------------------------------------- */

/**
 * The video behind this entry is gone — deleted, private, terminated or region-blocked — and the
 * endpoint says so by **blanking the row rather than omitting it**:
 *
 * ```json
 * { "type": "video", "title": "", "videoId": "R3rnxQBizoU", "author": "", "authorId": "",
 *   "authorUrl": "/channel/", "videoThumbnails": [ … ], "index": 84,
 *   "lengthSeconds": 0, "liveNow": true }
 * ```
 *
 * **559 of 8 101 rows across 117 playlists (6.9 %).** Six of them fetched individually answer `500`
 * with YouTube's own reason — *removed for violating Community Guidelines*, *removed following a
 * copyright removal request by SME*, *the YouTube account associated with this video has been
 * terminated*, *Sign in if you've been granted access to this video*.
 *
 * Keyed on the **title**, because that is the field whose emptiness makes the row unrenderable:
 * there is nothing to label a link with. R4 does not drop these — `parseVideoSummary` fails only on a
 * missing `videoId` — and it should not: a 241-entry playlist with 27 dead videos is 241 rows with 27
 * marked, not 214 rows and a numbering that silently skips.
 */
export function playlistEntryUnavailable(video: VideoSummary): boolean {
  return video.title.trim() === '';
}

/**
 * A row whose video is fine and whose **channel the endpoint did not scrape** — a real title and a
 * real duration next to `author: ""`, `authorId: ""`, `authorUrl: "/channel/"`.
 *
 * **892 of 8 101 rows (11 %)**, and the videos are perfectly healthy: `a01QQZyl-_I` is Queen's *Under
 * Pressure* and reports `author: "Queen Official"` when fetched on its own. So this is neither the
 * deleted-video shape above nor 1f's collaboration video (which has a *non-empty* `author` and the
 * same empty id) — it is a third state, separated from the first by nothing but the title being there.
 *
 * `channelRouteOf` already refuses to build a link from `authorUrl: "/channel/"`, so the byline does
 * not navigate to "Nothing here". What this adds is that there is no byline to draw at all: rendered
 * faithfully the row shows an empty span where a channel name goes, on one row in nine.
 */
export function playlistEntryAuthorMissing(video: VideoSummary): boolean {
  return !playlistEntryUnavailable(video) && video.author.trim() === '';
}

/**
 * What this row is allowed to state. {@link listingFacts} answers it for a feed; this adds the one
 * per-endpoint correction, and it is the sharpest one the project has measured.
 *
 * **`liveNow: true` on a playlist entry means the video is deleted.** Not "is live" — the two
 * populations are the same population:
 *
 * ```
 * 8 101 rows over 117 playlists
 *   liveNow === true                559
 *   title === "" (the dead shape)   559
 *   live AND dead                   559      ← every single one
 *   live but NOT dead                 0
 *   dead but NOT live                 0
 * ```
 *
 * A perfect correspondence in both directions, which makes the field worthless as a live signal here
 * and actively harmful: `videoBadgeOf` tests `liveNow` **first**, so rendered faithfully this draws a
 * red **LIVE** badge on 559 titleless cards pointing at videos YouTube has removed.
 *
 * This is 2d's `/streams` finding upside down. There `liveNow: false` lied about streams that were
 * live; here `liveNow: true` lies about videos that are gone. Both are the same lesson — the field is
 * a property of the *endpoint* that answered, not of the video — and both are why `channelRowFacts`
 * and this function take a tab or an endpoint rather than only a row.
 *
 * A genuinely live entry would therefore lose its badge, and that is the deliberate trade: n = 0 live
 * rows in 8 101 against 559 wrong badges. Such a row carries `lengthSeconds: 0` anyway, so what it
 * gets is *no* badge — "we do not know" — rather than a false duration.
 *
 * The other two fields need no arm at all, because a playlist entry **carries neither**: the row has
 * exactly ten keys (`type`, `title`, `videoId`, `author`, `authorId`, `authorUrl`, `videoThumbnails`,
 * `index`, `lengthSeconds`, `liveNow`) on 8 101 of 8 101 rows. No `viewCount`, no `published`, no
 * `publishedText`. So `listingFacts` reads them as the absences they are and the row draws neither —
 * a structural gap, not a degraded scrape, which is why {@link playlistScrape} does not report it.
 */
export function playlistEntryFacts(
  video: VideoSummary,
  nowEpochSec: number,
  scrape: FeedScrape | null,
): ListingFacts {
  const facts = listingFacts(video, nowEpochSec, scrape);
  if (!video.liveNow) return facts;
  return { ...facts, badge: videoBadgeOf({ ...video, liveNow: false }) };
}

/**
 * 2b's degeneracy reading, over the rows it can apply to.
 *
 * The unavailable rows are excluded on purpose: they have no duration *by construction*, so a
 * playlist of 30 videos where 27 are dead would otherwise be graded "the scrape produced titles only"
 * — a statement about the API when the truth is a statement about the playlist. With them out, a
 * `degenerate` verdict means what it does everywhere else: the response really did come back without
 * the fields it normally carries.
 */
export function playlistScrape(entries: readonly PlaylistEntry[], nowEpochSec: number): FeedScrape {
  const available = entries.flatMap((entry) =>
    playlistEntryUnavailable(entry.video) ? [] : [entry.video],
  );
  return feedScrape(available, nowEpochSec);
}

/**
 * When the playlist was last changed, or `null` when the value is not a date.
 *
 * `updated` is **day-granular** for a mirrored YouTube playlist — 18 of 20 values were exactly
 * midnight UTC — so it is a date and rendering a time from it would invent precision. The other two
 * were second-granular, and one of them was **6 seconds in the future**: a playlist Invidious had just
 * refreshed reports the request's own second, which is `publishedText`'s back-computation in a new
 * field (API reference §2). Rendered relative that is "now" or "in 6 seconds" about a playlist last
 * touched years ago.
 *
 * So the same tolerance a listing date gets, for the same reason and from the same constant.
 */
export function playlistUpdatedEpochSec(playlist: Playlist, nowEpochSec: number): number | null {
  const updated = playlist.updatedEpochSec;
  if (updated === null || !Number.isFinite(updated)) return null;
  // Reuses `feedRowHasDate`'s judgement rather than restating the threshold, so the two cannot drift.
  return feedRowHasDate({ publishedEpochSec: updated }, nowEpochSec) ? updated : null;
}

/**
 * A statement the page has to make about the list it is nonetheless showing — the shape
 * `screens/feed/model.ts` established in 2b and `channelTabNotes` followed in 2d.
 *
 * Each one is conditional on its measurement still holding, so it disappears the moment upstream
 * starts filling the field. That is the difference between explaining data and apologising for code.
 */
export type PlaylistNote =
  /** Entries whose video YouTube will not serve — {@link playlistEntryUnavailable}. */
  | { readonly id: 'unavailable-entries'; readonly count: number }
  /** Rows the endpoint returned without their channel — {@link playlistEntryAuthorMissing}. */
  | { readonly id: 'authors-missing'; readonly count: number }
  /** 2b's degenerate list, over the available rows only — {@link playlistScrape}. */
  | { readonly id: 'degenerate'; readonly rows: number }
  /** R4 — records we recognised and could not use. */
  | { readonly id: 'dropped'; readonly count: number }
  /**
   * The header's own count disagrees with what could be listed, and the list is finished. Genuine on
   * 11 of 12 playlists measured (575, 496, 437, 266, 210, 186, 180, 110, 48, 14, 13 — each matching a
   * gapless `0..count-1`), so a mismatch is worth stating rather than papering over. Only reported
   * once paging has run out: mid-fold every playlist "disagrees".
   */
  | { readonly id: 'count-mismatch'; readonly declared: number; readonly listed: number };

export function playlistNotes(
  playlist: Playlist,
  listing: PlaylistListing,
  nowEpochSec: number,
): readonly PlaylistNote[] {
  const notes: PlaylistNote[] = [];
  const videos = listing.entries.map((entry) => entry.video);

  const unavailable = videos.filter(playlistEntryUnavailable).length;
  if (unavailable > 0) notes.push({ id: 'unavailable-entries', count: unavailable });

  const authorless = videos.filter(playlistEntryAuthorMissing).length;
  if (authorless > 0) notes.push({ id: 'authors-missing', count: authorless });

  const scrape = playlistScrape(listing.entries, nowEpochSec);
  if (scrape.degenerate) notes.push({ id: 'degenerate', rows: scrape.rows });

  if (listing.droppedCount > 0) notes.push({ id: 'dropped', count: listing.droppedCount });

  const declared = playlist.videoCount;
  if (declared !== null && listing.nextPage === null && declared !== listing.entries.length) {
    notes.push({ id: 'count-mismatch', declared, listed: listing.entries.length });
  }

  return notes;
}
